import React, { useState, useEffect } from 'react';
import { Users, Bot, Phone, Activity, TrendingUp, Clock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { clientAPI } from '../../services/api';

const ClientDashboard = () => {
  const { clientUser } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalAgents: 0,
    activeAgents: 0,
    totalCalls: 0
  });
  const [recentAgents, setRecentAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [clientUser]);

  const fetchDashboardData = async () => {
    if (!clientUser?.id) return;
    
    try {
      // Fetch users for this sub-admin
      const usersRes = await clientAPI.getUsers(clientUser?.id);
      
      // Fetch agents for this sub-admin
      const agentsRes = await clientAPI.getAgents(clientUser?.id);

      const users = usersRes.data.users || [];
      const agents = agentsRes.data.agents || [];

      setStats({
        totalUsers: users.length,
        totalAgents: agents.length,
        activeAgents: agents.filter(a => a.status === 'active').length,
        totalCalls: agents.reduce((sum, a) => sum + (a.total_calls || 0), 0)
      });

      setRecentAgents(agents.slice(0, 5));
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { icon: Users, label: t('clientDashboard.totalUsers'), value: stats.totalUsers, color: 'from-blue-500 to-blue-600' },
    { icon: Bot, label: t('nav.voiceAgents'), value: stats.totalAgents, color: 'from-teal-500 to-teal-600' },
    { icon: Activity, label: t('clientDashboard.activeAgents'), value: stats.activeAgents, color: 'from-green-500 to-green-600' },
    { icon: Phone, label: t('clientDashboard.totalCalls'), value: stats.totalCalls, color: 'from-purple-500 to-purple-600' },
  ];

  return (
    <div className="p-8" data-testid="client-dashboard">
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-2">
          {t('dashboard.welcome', { name: clientUser?.name || t('sidebar.client') })}
        </h1>
        <p className="text-gray-400">{t('clientDashboard.subtitle')}</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 hover:border-gray-700 transition-colors"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <TrendingUp className="w-5 h-5 text-green-400" />
            </div>
            <p className="text-3xl font-bold text-white mb-1">
              {loading ? '-' : stat.value}
            </p>
            <p className="text-gray-400 text-sm">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Recent Agents */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">{t('clientDashboard.recentAgents')}</h3>
          {recentAgents.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              {t('clientDashboard.noAgentsYet')}
            </p>
          ) : (
            <div className="space-y-3">
              {recentAgents.map((agent) => (
                <div
                  key={agent.id}
                  className="flex items-center justify-between p-3 bg-[#1a1a1f] rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
                      <Bot className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{agent.name}</p>
                      <p className="text-gray-500 text-sm">{agent.language || 'English'}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    agent.status === 'active' 
                      ? 'bg-green-400/10 text-green-400' 
                      : 'bg-gray-400/10 text-gray-400'
                  }`}>
                    {agent.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">{t('dashboard.quickActions')}</h3>
          <div className="space-y-3">
            <a
              href="/client/agents"
              className="flex items-center gap-3 p-4 bg-[#1a1a1f] rounded-lg hover:bg-[#252530] transition-colors"
            >
              <div className="w-10 h-10 rounded-lg bg-teal-400/10 flex items-center justify-center">
                <Bot className="w-5 h-5 text-teal-400" />
              </div>
              <div>
                <p className="text-white font-medium">{t('voiceAgents.createAgent')}</p>
                <p className="text-gray-500 text-sm">{t('clientDashboard.createAgentDesc')}</p>
              </div>
            </a>
            <a
              href="/client/users"
              className="flex items-center gap-3 p-4 bg-[#1a1a1f] rounded-lg hover:bg-[#252530] transition-colors"
            >
              <div className="w-10 h-10 rounded-lg bg-blue-400/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-white font-medium">{t('clientDashboard.manageUsers')}</p>
                <p className="text-gray-500 text-sm">{t('clientDashboard.manageUsersDesc')}</p>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;
